import { Component } from '@theme/component';

/**
 * <tmc-vitrine>: a row of lit glass display cases you swipe through. Native horizontal scroll with
 * centre snap does the moving (every device, no-JS too); this adds the gallery lighting: each case gets
 * --tmc-focus (1 when centred, 0 one case away) so CSS can light the centred case, dim and step back the
 * neighbours, shift the glass glare and let the piece sit a little deeper than the glass (parallax).
 * Also drives the "03 / 10" counter and the desktop arrow buttons.
 *
 * reduced / off tier: no focus effects (every case lit, nothing scales); arrows and counter still work.
 *
 * @typedef {{ track: HTMLElement, count?: HTMLElement, prev?: HTMLButtonElement, next?: HTMLButtonElement }} Refs
 * @extends {Component<Refs>}
 */
class TmcVitrine extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | null} */
  #abort = null;
  #frame = 0;
  #effects = true;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    const { track, prev, next } = this.refs;
    const tier = /** @type {any} */ (window).TMC?.motion?.tier?.(this.dataset.motion);
    this.#effects = !matchMedia('(prefers-reduced-motion: reduce)').matches && tier !== 'off' && this.dataset.motion !== 'off';
    this.toggleAttribute('data-effects', this.#effects);
    this.dataset.enhanced = '';

    track.addEventListener('scroll', () => this.#queue(), { passive: true, signal });
    window.addEventListener('resize', () => this.#queue(), { passive: true, signal });
    prev?.addEventListener('click', () => this.#step(-1), { signal });
    next?.addEventListener('click', () => this.#step(1), { signal });
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
  }

  /** @returns {HTMLElement[]} */
  #cases() {
    return /** @type {HTMLElement[]} */ ([...this.refs.track.children]);
  }

  /** Moves one case left or right (snap centres it). @param {1 | -1} direction */
  #step(direction) {
    const cases = this.#cases();
    const target = cases[Math.min(cases.length - 1, Math.max(0, this.#current() + direction))];
    target?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
  }

  #current() {
    const { track } = this.refs;
    const centre = track.getBoundingClientRect().left + track.clientWidth / 2;
    let best = 0;
    let bestDistance = Infinity;
    this.#cases().forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - centre);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  #queue() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  }

  #update() {
    const { track, count, prev, next } = this.refs;
    const cases = this.#cases();
    const centre = track.getBoundingClientRect().left + track.clientWidth / 2;
    if (this.#effects) {
      for (const item of cases) {
        const rect = item.getBoundingClientRect();
        const offset = (rect.left + rect.width / 2 - centre) / Math.max(rect.width, 1);
        const focus = Math.max(0, 1 - Math.abs(offset));
        item.style.setProperty('--tmc-focus', focus.toFixed(3));
        item.style.setProperty('--tmc-offset', Math.max(-1.5, Math.min(1.5, offset)).toFixed(3));
      }
    }
    const current = this.#current();
    cases.forEach((item, index) => item.toggleAttribute('data-current', index === current));
    if (count) count.textContent = `${String(current + 1).padStart(2, '0')} / ${String(cases.length).padStart(2, '0')}`;
    if (prev) prev.disabled = current === 0;
    if (next) next.disabled = current === cases.length - 1;
  }
}

// Registered last: defining it upgrades elements already on the page at once.
if (!customElements.get('tmc-vitrine')) customElements.define('tmc-vitrine', TmcVitrine);
